import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Nav, Footer, ClosingV5 } from "../App";
import { Eyebrow, PillarAccent } from "../components/atoms";
import { useContactPanel } from "../components/ContactPanel";
import BuildPipelineMockup from "../components/BuildPipelineMockup";
import { useSeo } from "../lib/useSeo";
import { useContent } from "../lib/useContent";
import {
  TrustStripDev,
  WhatYouGet,
  Capabilities,
  BuildShowcase,
  Possibilities,
  Process,
  TechStack,
  WhoWeWorkWith,
} from "./ShopifyDev";

/* ============================================================
   /shopify-experts-development-v2
   Variant of the Shopify Development page.
   Hero right-column replaced with BuildPipelineMockup
   (Code → Wireframe → Render → Convert).
   Everything else inherits from the original page.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
  </button>
);

const ShopifyDevV2Hero = ({ onPrimary }) => {
  const cms = useContent("shopify-dev");
  const h = cms?.hero || {};
  const breadcrumb = h.breadcrumb || "Shopify Development";
  const eyebrow = h.eyebrow || "eCommerce · Shopify & Shopify Plus";
  const titleLine1 = h.title_line_1 || "Built on Shopify.";
  const titleEmphasis = h.title_emphasis || "Engineered";
  const titleLine2 = h.title_line_2 || "to scale";
  const subtitle = h.subtitle ||
    "One boutique team, every layer of Shopify. From storefront craft to checkout engineering, we partner with brands ready to move beyond templates and app bloat.";
  const ctaLabel = h.cta_primary_label || h.cta_label || "Start a Project";
  const secondaryLabel = h.cta_secondary_label || "See capabilities";
  const secondaryHref = h.cta_secondary_href || "#capabilities";
  return (
  <section
    id="top"
    data-testid="dev-v2-hero"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    {/* Editorial grid backdrop */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none opacity-[0.35]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(244,214,78,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(244,214,78,0.045) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
        maskImage: "radial-gradient(900px 600px at 70% 20%, black, transparent 70%)",
        WebkitMaskImage:
          "radial-gradient(900px 600px at 70% 20%, black, transparent 70%)",
      }}
    />
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(1100px 600px at 80% 0%, rgba(244,214,78,0.08), transparent 60%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
      {/* Breadcrumb */}
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-10 md:mb-14"
      >
        <a href="/" className="hover:text-[#F4D64E] transition-colors">Sunday</a>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">{breadcrumb}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-center">
        {/* LEFT — copy (identical to v1) */}
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>

          <h1
            data-testid="dev-v2-hero-title"
            className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[4.75rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
          >
            <span data-reveal="mask-block">
              <span>{titleLine1}</span>
            </span>
            <span data-reveal="mask-block" style={{ "--i": 1 }} className="block">
              <span>
                <span className="italic font-normal text-neutral-400">
                  {titleEmphasis}
                </span>{" "}
                <PillarAccent>{titleLine2}</PillarAccent>.
              </span>
            </span>
          </h1>

          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-9 max-w-[52ch] text-[1.02rem] md:text-[1.15rem] text-neutral-300 leading-[1.6]"
          >
            <span>{subtitle}</span>
          </p>

          <div
            data-reveal="eyebrow"
            style={{ "--i": 3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <PrimaryCTA testId="dev-v2-hero-cta" onClick={onPrimary}>
              {ctaLabel}
            </PrimaryCTA>
            <a
              href={secondaryHref}
              className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
            >
              {secondaryLabel}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>

        {/* RIGHT — pipeline mockup (the V2 swap) */}
        <div className="lg:col-span-5">
          <BuildPipelineMockup />
        </div>
      </div>
    </div>
  </section>
  );
};

export default function ShopifyDevV2() {
  const { open: openContact } = useContactPanel();

  useSeo({
    route: "/shopify-experts-development",
    fallback: {
      title: "Shopify Development — Sunday Digital",
      description: "Custom Shopify Plus development. Themes, apps, migrations, headless storefronts.",
    },
  });

  useEffect(() => {
    document.title = "Shopify Development · v2 — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  /* Reveal observer — same setup as HomeV5 */
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="shopify-dev-v2-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <ShopifyDevV2Hero onPrimary={openContact} />
        <TrustStripDev />
        <WhatYouGet />
        <Capabilities />
        <BuildShowcase />
        <Possibilities />
        <Process />
        <TechStack />
        <WhoWeWorkWith onPrimary={openContact} />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
